/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';

import { pageEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { RESPONSE_TYPES } from '../../../ia/util/validation/constants';
import { getFirstVisibleAncestorOrSelf } from '../../util/dialog/DialogTreeUtil';
import * as actionType from './actionTypes';
import { getNextCalloutId, setResponseNodesAttribute } from './selectors';

export const initialState = {
  //sort alphabetically for easy of redux debugging!
  childMap: {},
  handlerMap: {},
  highlightedNodes: {},
  loading: true,
  jumpToMode: {
    isActive: false
  },
  nextSiblingMap: {},
  nodeMap: {},
  pendingUpdatesMap: {},
  responseMap: {},
  selectedNodeId: null,
  slotMap: {}
};

const clearNodes = (state, preserveSelectedNode) => {
  const newState = _.cloneDeep(initialState);
  newState.selectedNodeId = preserveSelectedNode ? state.selectedNodeId : null;
  return newState;
};

// ADD NODE
const addNodeStart = (state, node, select) => {
  node.created = node.created || new Date().toISOString();
  const newState = {
    ...state,
    childMap: _.cloneDeep(state.childMap),
    handlerMap: _.cloneDeep(state.handlerMap),
    nextSiblingMap: _.cloneDeep(state.nextSiblingMap),
    nodeMap: _.cloneDeep(state.nodeMap),
    responseMap: _.cloneDeep(state.responseMap),
    slotMap: _.cloneDeep(state.slotMap)
  };

  // Add placeholder node
  node.loading = true;
  addNodeHelper(newState, node, select);
  return newState;
};

const addNodeError = (state, node) => {
  const newState = {
    ...state,
    childMap: _.cloneDeep(state.childMap),
    handlerMap: _.cloneDeep(state.handlerMap),
    nodeMap: _.cloneDeep(state.nodeMap),
    responseMap: _.cloneDeep(state.responseMap),
    slotMap: _.cloneDeep(state.slotMap)
  };

  deleteNodeHelper(newState, node, true, true);
  return newState;
};

const addNodeSuccess = (state, node, select) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap)
  };
  // Update with node from backend
  node.pendingUpdates = 0;
  node.loading = false;
  newState.nodeMap[node.dialog_node] = node;
  // Select and expand
  if (select) {
    selectNodeHelper(newState, node.dialog_node, select, true);
  }
  return newState;
};

// ADD NODES
const addNodes = (state, nodes) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap),
    childMap: _.cloneDeep(state.childMap),
    handlerMap: _.cloneDeep(state.handlerMap),
    nextSiblingMap: _.cloneDeep(state.nextSiblingMap),
    responseMap: _.cloneDeep(state.responseMap),
    slotMap: _.cloneDeep(state.slotMap),
    isUserLabelUsed: containNodeWithUserLabel(nodes),
    loading: false,
    lastFetched: Date.now()
  };
  addNodesHelper(newState, nodes);
  return newState;
};
const addNodesCache = state => {
  const newState = {
    ...state,
    loading: false
  };
  return newState;
};

const containNodeWithUserLabel = nodes => {
  return nodes.some(function (node) {
    return node.user_label;
  });
};

const generateNodeId = node => {
  let idPrefix = 'node_';
  if (node.type === 'response_condition') {
    idPrefix = 'response_';
  } else if (node.type === 'slot') {
    idPrefix = 'slot_';
  } else if (node.type === 'event_handler') {
    idPrefix = 'handler_';
  }
  return _.uniqueId(idPrefix) + '_' + new Date().getTime();
};

const isProperNode = node => {
  if (!node) {
    return false;
  }
  if (
    !node.type ||
    node.type === 'standard' ||
    node.type === 'frame' ||
    node.type === 'folder'
  ) {
    return true;
  }
  return false;
};

const getSiblingMapOfType = (state, type) => {
  switch (type) {
    case 'response_condition':
      return state.responseMap;
    case 'slot':
      return state.slotMap;
    case 'event_handler':
      return state.handlerMap;
    default:
      return state.childMap;
  }
};

const addNodeIntoSiblingMapHelper = (newState, node) => {
  const nodeMap = newState.nodeMap;
  const siblingMap = getSiblingMapOfType(newState, node.type);
  const parentId = node.parent;

  const siblings = siblingMap[parentId] || [];

  let index = 0;
  let precedingNode = node;

  if (node.previous_sibling) {
    do {
      if (!precedingNode.previous_sibling) {
        break;
      }
      precedingNode = nodeMap[precedingNode.previous_sibling];
      if (
        precedingNode &&
        ((isProperNode(precedingNode) && isProperNode(node)) ||
          precedingNode.type === node.type)
      ) {
        index = siblings.indexOf(precedingNode.dialog_node);
        if (index > -1) {
          index += 1;
          break;
        }
      }
    } while (precedingNode);
  }

  siblings.splice(index, 0, node.dialog_node);
  siblingMap[parentId] = siblings;
};

const deleteNodeFromSiblingMap = (state, node) => {
  const siblingMap = getSiblingMapOfType(state, node.type);
  const parentId = node.parent;
  const siblings = siblingMap[parentId] || [];

  const index = siblings.findIndex(nodeId => nodeId === node.dialog_node);

  if (index > -1) {
    siblings.splice(index, 1);
  }

  siblingMap[parentId] = siblings;

  if (siblings.length === 0) {
    delete siblingMap[parentId];
  }
};

const addNodesHelper = (newState, nodes) => {
  nodes.forEach(node => {
    newState.nodeMap[node.dialog_node] = node;
  });

  nodes.forEach(node => {
    addNodeHelper(newState, node);
  });
};

const addNodeHelper = (newState, node, select) => {
  // Ensure node is complete
  if (!node.dialog_node) {
    node.dialog_node = generateNodeId(node);
  }
  if (!node.type) {
    node.type = 'standard';
  }

  // Add to maps
  newState.nodeMap[node.dialog_node] = node;

  // Select and expand
  if (select) {
    selectNodeHelper(newState, node.dialog_node, select, true);
  }

  addNodeIntoSiblingMapHelper(newState, node);

  // Update previous_sibling of next sibling

  const nextSiblingId =
    newState.nextSiblingMap[
      getNextSiblingMapKey(node.parent, node.previous_sibling)
    ];
  if (nextSiblingId) {
    const nextSibling = newState.nodeMap[nextSiblingId];
    if (nextSibling) {
      nextSibling.previous_sibling = node.dialog_node;
      newState.nextSiblingMap[
        getNextSiblingMapKey(node.parent, node.previous_sibling)
      ] = node.dialog_node;
      newState.nextSiblingMap[
        getNextSiblingMapKey(node.parent, node.dialog_node)
      ] = nextSibling.dialog_node;
    }
  } else {
    newState.nextSiblingMap[
      getNextSiblingMapKey(node.parent, node.previous_sibling)
    ] = node.dialog_node;
  }
  return newState;
};

const getNextSiblingMapKey = (key, key2) => {
  const KEY_PLACEHOLDER = '_';
  return (key ? key : KEY_PLACEHOLDER) + '/' + (key2 ? key2 : KEY_PLACEHOLDER);
};

const selectNodeHelper = (newState, nodeId, select, expand) => {
  const node = newState.nodeMap[nodeId];
  node.selected = select;
  if (newState.selectedNodeId && newState.selectedNodeId !== nodeId) {
    let nodeToDeselect = newState.nodeMap[newState.selectedNodeId];
    if (nodeToDeselect) {
      nodeToDeselect.selected = false;
    }
    while (
      nodeToDeselect &&
      nodeToDeselect.type &&
      nodeToDeselect.type !== 'folder' &&
      nodeToDeselect.type !== 'frame' &&
      nodeToDeselect.type !== 'standard'
    ) {
      nodeToDeselect = newState.nodeMap[nodeToDeselect.parent];
      nodeToDeselect.selected = false;
    }
  }
  newState.selectedNodeId = select ? nodeId : null;

  // If selected node is a subtype then mark it's real node
  let nodeToSelect = node;
  while (
    nodeToSelect &&
    nodeToSelect.type &&
    nodeToSelect.type !== 'folder' &&
    nodeToSelect.type !== 'frame' &&
    nodeToSelect.type !== 'standard'
  ) {
    nodeToSelect = newState.nodeMap[nodeToSelect.parent];
    nodeToSelect.selected = true;
  }

  if (expand && node.parent) {
    newState.nodeMap[node.parent].expanded = true;
  }
  return newState;
};

// DELETE NODE
const deleteNodeStart = (state, node) => {
  const newState = {
    ...state
  };
  node.loading = true;
  node.expanded = false;
  return newState;
};

const deleteNodeSuccess = (state, node, childNodesToPreserve) => {
  const newState = {
    ...state,
    childMap: _.cloneDeep(state.childMap),
    handlerMap: _.cloneDeep(state.handlerMap),
    nextSiblingMap: _.cloneDeep(state.nextSiblingMap),
    nodeMap: _.cloneDeep(state.nodeMap),
    responseMap: _.cloneDeep(state.responseMap),
    slotMap: _.cloneDeep(state.slotMap)
  };

  if (childNodesToPreserve && childNodesToPreserve.length > 0) {
    let prevNode = node;
    childNodesToPreserve
      .filter(childNode => childNode.parent === node.dialog_node)
      .forEach(childNode => {
        moveNodeHelper(newState, childNode, prevNode, 'below_node');
        prevNode = childNode;
      });
  }

  deleteNodeHelper(newState, node, false, true);
  return newState;
};

const deleteJumpToHelper = (newState, node) => {
  const deletedNodeId = node.dialog_node;
  for (const nodeId in newState.nodeMap) {
    const node = newState.nodeMap[nodeId];
    if (node.next_step && node.next_step.dialog_node === deletedNodeId) {
      node.next_step = null;
    }
  }
};

const deleteNodeHelper = (
  newState,
  node,
  leaveJumpTos,
  deleteAllDescendants
) => {
  if (newState.selectedNodeId === node.dialog_node) {
    newState.selectedNodeId = null;
  }

  if (deleteAllDescendants === true) {
    const allDescendants = getDescendants(newState, node, true);
    allDescendants.forEach(descendant =>
      deleteNodeHelper(newState, descendant, leaveJumpTos, false)
    );
  }

  deleteNodeFromSiblingMap(newState, node);

  // Deleted a proper node so need to check for Jump to references to it
  if (isProperNode(node) && !leaveJumpTos) {
    deleteJumpToHelper(newState, node);
  }

  // Update parent information if last child is removed
  if (
    node.parent &&
    newState.nodeMap[node.parent] &&
    !newState.childMap[node.parent]
  ) {
    newState.nodeMap[node.parent].expanded = false;
  }

  // Delete the node
  delete newState.nodeMap[node.dialog_node];

  // Update previous_sibling of next sibling
  const nextSiblingId =
    newState.nextSiblingMap[
      getNextSiblingMapKey(node.parent, node.dialog_node)
    ];
  if (nextSiblingId) {
    const nextSibling = newState.nodeMap[nextSiblingId];
    if (nextSibling) {
      nextSibling.previous_sibling = node.previous_sibling;
      newState.nextSiblingMap[
        getNextSiblingMapKey(node.parent, node.previous_sibling)
      ] = nextSibling.dialog_node;
    }
  } else {
    delete newState.nextSiblingMap[
      getNextSiblingMapKey(node.parent, node.previous_sibling)
    ];
  }
  delete newState.nextSiblingMap[
    getNextSiblingMapKey(node.parent, node.dialog_node)
  ];

  return newState;
};

const expandNode = (state, nodeId, expanded) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap)
  };
  if (newState.nodeMap[nodeId]) {
    newState.nodeMap[nodeId].expanded = !!expanded;
  }
  return newState;
};

const expandPath = (state, path, expanded) => {
  const newState = {
    ...state,
    nodeMap: state.__nodeMapCloned__
      ? state.nodeMap
      : _.cloneDeep(state.nodeMap)
  };
  for (let i = 0; i < path.length; i++) {
    newState.nodeMap[path[i]].expanded = !!expanded;
  }
  return newState;
};

const selectNode = (state, nodeId, selected, nodeIdsToHighlight) => {
  const newState = {
    ...state,
    jumpToMode: { ...state.jumpToMode },
    nodeMap: state.__nodeMapCloned__
      ? state.nodeMap
      : _.cloneDeep(state.nodeMap),
    pendingUpdatesMap: { ...state.pendingUpdatesMap }
  };

  if (!state.nodeMap[nodeId]) {
    return newState;
  }

  selectNodeHelper(newState, nodeId, nodeIdsToHighlight ? false : selected);
  if (!selected) {
    newState.highlightedNodes = {};
  } else {
    // selected is true so we can set the selectedNodeId
    newState.highlightedNodes = highlightNodes(nodeIdsToHighlight);
    newState.selectedNodeId = nodeId;
  }

  const prevSelectedTreeNode =
    getFirstVisibleAncestorOrSelf(state.selectedNodeId, state.nodeMap) || {};
  const selectedTreeNode =
    getFirstVisibleAncestorOrSelf(nodeId, state.nodeMap) || {};

  if (
    !selected ||
    prevSelectedTreeNode.dialog_node !== selectedTreeNode.dialog_node
  ) {
    setTimeout(function () {
      if (selected) {
        pageEvents.dialogNodeEdit();
      } else {
        pageEvents.dialogNodeClose();
      }
    }, 700);
  }

  return newState;
};

const selectNodes = (state, nodesWithAncestors, nodeIdsToHighlight) => {
  let newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap),
    // This is a temporary flag for the process in the loop below.
    // In the loop, the state is processed by three selectors
    // where they all deep-clone the nodeMap again.
    // This causes performance issue when the nodeMap is large.
    // Adding this flag in order not to clone nodeMap everytime
    // to improve the performance.
    __nodeMapCloned__: true
  };

  for (let i = 0; i < nodesWithAncestors.length; i++) {
    const ancestors = nodesWithAncestors[i].ancestors;
    const nodeId = nodesWithAncestors[i].nodeId;

    newState = selectNode(newState, nodeId, false);
    newState = expandPath(newState, ancestors, true);
    newState = selectNode(newState, nodeId, true, nodeIdsToHighlight);
  }

  delete newState.__nodeMapCloned__;

  return newState;
};

const selectNodeWithErrors = (state, nodeId) => {
  const { nodeMap } = state;
  if (
    nodeMap[nodeId] &&
    nodeMap[nodeId].type !== 'standard' &&
    nodeMap[nodeId].type !== 'frame'
  ) {
    let node = nodeMap[nodeId];
    while (node && node.type !== 'standard' && node.type !== 'frame') {
      const parentId = node.parent;
      node = nodeMap[parentId];
    }
    if (node) {
      return selectNode(state, node.node_id, true);
    }
  }
  return selectNode(state, nodeId, true);
};

const highlightNodes = nodeIds => {
  const highlightedNodes = {};
  if (!nodeIds) {
    return highlightedNodes;
  }
  for (let i = 0; i < nodeIds.length; i++) {
    highlightedNodes[nodeIds[i]] = true;
  }
  return highlightedNodes;
};

// UPDATE NODE
const updateNodeStart = (
  state,
  nodeId,
  useNewIAUpdatedAccess,
  assistantId,
  node
) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap),
    pendingUpdatesMap: _.cloneDeep(state.pendingUpdatesMap)
  };

  const updatedNode = { ...node };
  turnOnLoadingHelper(newState, nodeId);
  updateNodeHelper(newState, nodeId, updatedNode);
  return newState;
};

const updateNodeSuccess = (state, nodeId, node) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap),
    pendingUpdatesMap: _.cloneDeep(state.pendingUpdatesMap)
  };
  turnOffLoadingHelper(newState, nodeId);
  updateNodeHelper(newState, nodeId, node);
  return newState;
};

const updateNodeError = (state, nodeId) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap),
    pendingUpdatesMap: _.cloneDeep(state.pendingUpdatesMap)
  };

  turnOffLoadingHelper(newState, nodeId);
  return newState;
};

// UPDATE NODES
const updateNodes = (state, updatedNodes) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap)
  };

  for (let i = 0; i < updatedNodes.length; i++) {
    updateNodeHelper(newState, updatedNodes[i].dialog_node, updatedNodes[i]);
  }

  return newState;
};

const turnOnLoadingHelper = (state, nodeId) => {
  const { pendingUpdatesMap, nodeMap } = state;

  pendingUpdatesMap[nodeId] = (pendingUpdatesMap[nodeId] || 0) + 1;

  if (nodeId in nodeMap) {
    nodeMap[nodeId].loading = true;
  }
};

const turnOffLoadingHelper = (state, nodeId) => {
  const { pendingUpdatesMap, nodeMap } = state;

  pendingUpdatesMap[nodeId] = Math.max((pendingUpdatesMap[nodeId] || 0) - 1, 0);

  if (nodeId in nodeMap) {
    nodeMap[nodeId].loading = pendingUpdatesMap[nodeId] > 0;
  }
};

const updateNodeHelper = (state, nodeId, updatedNode) => {
  const node = state.nodeMap[nodeId];
  if (node) {
    for (const prop in updatedNode) {
      if (prop === 'next_step' && !node.next_step) {
        node.expanded = true;
      }
      node[prop] = updatedNode[prop];
      if (prop === 'title') {
        if (updatedNode.title) {
          node.name = updatedNode.title;
        } else {
          node.name = updatedNode.conditions;
        }
      }
    }
    state.nodeMap[nodeId] = node;
  }
  return state;
};

// MOVE NODE
const moveNodeStart = (state, sourceNode, targetNode, where) => {
  const newState = {
    jumpToMode: { ...state.jumpToMode },
    nodeMap: { ...state.nodeMap },
    childMap: _.cloneDeep(state.childMap),
    responseMap: state.responseMap,
    slotMap: state.slotMap,
    handlerMap: state.handlerMap,
    nextSiblingMap: state.nextSiblingMap,
    pendingUpdatesMap: { ...state.pendingUpdatesMap },
    selectedNodeId: state.selectedNodeId,
    highlightedNodes: state.highlightedNodes,
    isUserLabelUsed: state.isUserLabelUsed
  };
  sourceNode.loading = true;
  moveNodeHelper(newState, sourceNode, targetNode, where);

  const updatedNode = {
    parent: sourceNode.parent,
    previous_sibling: sourceNode.previous_sibling,
    digress_in: sourceNode.digress_in
  };

  updateNodeHelper(newState, sourceNode.dialog_node, updatedNode);
  return newState;
};

const moveNodeSuccess = (state, nodeId) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap)
  };
  const node = {
    loading: false
  };
  updateNodeHelper(newState, nodeId, node);
  return newState;
};

const moveNodeError = (state, sourceNode, undoNode) => {
  const newState = {
    jumpToMode: { ...state.jumpToMode },
    nodeMap: { ...state.nodeMap },
    childMap: _.cloneDeep(state.childMap),
    responseMap: { ...state.responseMap },
    slotMap: { ...state.slotMap },
    handlerMap: { ...state.handlerMap },
    nextSiblingMap: { ...state.nextSiblingMap },
    pendingUpdatesMap: { ...state.pendingUpdatesMap },
    selectedNodeId: state.selectedNodeId,
    highlightedNodes: state.highlightedNodes,
    isUserLabelUsed: state.isUserLabelUsed
  };

  deleteNodeHelper(newState, sourceNode);
  _.assign(sourceNode, undoNode);
  sourceNode.loading = false;
  addNodeHelper(newState, sourceNode, true);
  return newState;
};

const moveNodeHelper = (newState, sourceNode, targetNode, where) => {
  deleteNodeHelper(newState, sourceNode, true);

  if (where === 'as_child_node') {
    sourceNode.parent = targetNode.dialog_node;
    delete sourceNode.previous_sibling;
    const childNodes = newState.childMap[targetNode.dialog_node];
    if (childNodes && childNodes.length > 0) {
      for (let i = childNodes.length - 1; i >= 0; i--) {
        const previousNode = newState.nodeMap[childNodes[i]];
        if (
          !previousNode ||
          (previousNode.conditions !== 'true' &&
            previousNode.conditions !== 'anything_else')
        ) {
          sourceNode.previous_sibling = childNodes[i];
          break;
        }
      }
    }
  } else if (where === 'above_node') {
    sourceNode.parent = targetNode.parent;
    sourceNode.previous_sibling = targetNode.previous_sibling;
  } else {
    sourceNode.parent = targetNode.parent;
    sourceNode.previous_sibling = targetNode.dialog_node;
  }

  addNodeHelper(newState, sourceNode, true);

  if (sourceNode.parent) {
    sourceNode.digress_in = null;
  } else {
    sourceNode.digress_in = sourceNode.digress_in || 'does_not_return';
  }
};

// DUPLICATE NODE
const duplicateNodeStart = (state, sourceNode, newNode, allDuplicates) => {
  const newState = {
    ...state,
    childMap: _.cloneDeep(state.childMap),
    handlerMap: _.cloneDeep(state.handlerMap),
    nextSiblingMap: _.cloneDeep(state.nextSiblingMap),
    nodeMap: _.cloneDeep(state.nodeMap),
    responseMap: _.cloneDeep(state.responseMap),
    slotMap: _.cloneDeep(state.slotMap)
  };

  duplicateNodeHelper(newState, sourceNode, newNode, allDuplicates);
  const selfCopy = allDuplicates[0];
  const descendantsCopy =
    allDuplicates.length > 1 ? allDuplicates.slice(1) : [];

  addNodesHelper(newState, descendantsCopy);

  selfCopy.selected = false;
  updateNodeHelper(newState, newNode.dialog_node, selfCopy);

  return newState;
};

const duplicateNodeHelper = (state, sourceNode, newNode, allDuplicates) => {
  copyName(state, sourceNode, newNode);
  copyNodes(state, sourceNode, newNode, allDuplicates);
};

// Generate the name for this node copy.  New name is
// '<old-name> - copyN', where N is one greater than any
// other name copies that exist among the current siblings.
const copyName = (state, sourceNode, newNode) => {
  let nameBase = null;
  if (sourceNode.title) {
    nameBase = sourceNode.title + ' - copy';
  } else {
    nameBase = 'copy';
  }
  //escape all regex 'sensitive' chars (*.\/^ etc) in the title
  const sanitizedBase = nameBase.replace(
    // eslint-disable-next-line no-useless-escape
    /\[|\]|\-|\.|\\|\+|\*|\?|\^|\$|\(|\)|\{|\}|\=|\!|\<|\>|\|/gi,
    '\\$&'
  );
  const matcher = new RegExp('^' + sanitizedBase + "[' ']?(\\d+)$");
  const siblingsIds = state.childMap[sourceNode.parent] || [];
  const siblings = idsToNodes(siblingsIds, state.nodeMap);
  let copyN = 1;

  for (const { title } of siblings) {
    // We have a set of sibling nodes named as follows:
    // - my node
    // - my node - copy 1
    // - my node - copy 5
    // - my node - copy 2
    // - my node - copy 4
    // The user wants to duplicate 'my node'
    // With the following loop we iterate over each node and identify if the node's
    // title matches our pattern (title - copyN).
    // When we encounter 'my node - copy 1' we see that copyN (1) is greater than/equals to the
    // index identified. We increment copyN to 2 and move to the next node where we will find
    // again that copyN is less than '5', so we increment copyN to 6. At this point we will
    // iterate over the remaining nodes and not need to increment further.
    const match = matcher.exec(title);
    if (match && match[1] >= copyN) {
      copyN = Number(match[1]) + 1;
    }
  }
  newNode.name = newNode.title = `${nameBase} ${copyN}`;
};

// Make duplicates of all the nodes we need to copy and patch them
// up so that:
// 1. They have new identities
// 2. They refer to the new identity of any nodethey reference that
//    is part of the duplicated set.
const copyNodes = (state, sourceNode, newNode, allDuplicates) => {
  const descendantsAndSelf = getDescendants(state, sourceNode, true);
  descendantsAndSelf.unshift(sourceNode);

  // Make a first pass through the nodes we want to duplicate generating new
  // ids andmapping against the old ids.
  const idMap = {};

  for (const node of descendantsAndSelf) {
    let newId = null;
    if (node.dialog_node === sourceNode.dialog_node) {
      newId = newNode.dialog_node;
    } else {
      newId = generateNodeId(node);
    }
    idMap[node.dialog_node] = newId;
  }

  // Make a second pass through the nodes, copying them and assigning the
  // new ids.  We also patch up any referenced ids that are part of the
  // set of to-be-copied nodes.
  const nodes = [];
  for (const node of descendantsAndSelf) {
    const copyNode = _.cloneDeep(node);
    copyNode.dialog_node = copyNode.node_id = idMap[node.dialog_node];

    // Patch the properties that may refer to other nodess within our copy set
    if (node.parent && idMap.hasOwnProperty(node.parent)) {
      copyNode.parent = idMap[node.parent];
    }
    if (node.previous_sibling && idMap.hasOwnProperty(node.previous_sibling)) {
      copyNode.previous_sibling = idMap[node.previous_sibling];
    }
    if (
      node.next_step &&
      node.next_step.dialog_node &&
      idMap.hasOwnProperty(node.next_step.dialog_node)
    ) {
      copyNode.next_step.dialog_node = idMap[node.next_step.dialog_node];
    }

    if (node.type === 'folder') {
      copyNode.output = undefined;
    }

    if (node.dialog_node === sourceNode.dialog_node) {
      copyNode.dialog_node = newNode.dialog_node;
      copyNode.title = newNode.title;
      copyNode.parent = newNode.parent;
      copyNode.previous_sibling = newNode.previous_sibling;
      nodes.unshift(copyNode); // Keep our root node at the start of the array, for neatness
    } else {
      // Special cases: we need to ensure 'slot' nodes always have a 'variable' property.
      if (node.type === 'slot') {
        if (!copyNode.variable) {
          copyNode.variable = null;
        }
      }
      nodes.push(copyNode);
    }
  }

  allDuplicates.push(...nodes);
};

const getDescendants = (state, node, all) => {
  var descendants = [];
  gatherNodes(state, descendants, [node], all);
  descendants.shift();
  return descendants;
};

// Recursive function to gather all descendant nodes
// all=false: just include child nodes
// all=true: include related slots/handlers/responses
const gatherNodes = (state, result, nodes, all) => {
  for (let i = 0; i < nodes.length; i++) {
    result.push(nodes[i]);
    const childrenIds = state.childMap[nodes[i].dialog_node];
    const children = idsToNodes(childrenIds, state.nodeMap);
    if (children) {
      gatherNodes(state, result, children, all);
    }
    if (all) {
      const subNodesIds = getSubNodes(state, nodes[i].dialog_node);
      const subNodes = idsToNodes(subNodesIds, state.nodeMap);
      if (subNodes) {
        gatherNodes(state, result, subNodes, all);
      }
    }
  }
  return result;
};

const getSubNodes = (state, nodeId) => {
  var result = [];
  if (state.slotMap[nodeId]) {
    const slotIds = state.slotMap[nodeId];
    result.push(...slotIds);
  }
  if (state.handlerMap[nodeId]) {
    const handlerIds = state.handlerMap[nodeId];
    result.push(...handlerIds);
  }
  if (state.responseMap[nodeId]) {
    const responseIds = state.responseMap[nodeId];
    result.push(...responseIds);
  }
  return result.length ? result : null;
};

const idsToNodes = (ids, nodeMap) => {
  if (!ids) {
    return undefined;
  }
  const result = [];
  for (const id of ids) {
    const node = nodeMap[id];
    result.push(node);
  }
  return result;
};

const duplicateNodeSuccess = (state, nodeId) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap)
  };

  const node = {
    loading: false
  };
  updateNodeHelper(newState, nodeId, node);

  return newState;
};

const duplicateNodeError = (state, newNode) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap)
  };

  newState.nodeMap[newNode.dialog_node].loading = false;

  return newState;
};

// Add SLOT
const addSlotStart = (state, slot) => {
  const newState = {
    ...state,
    childMap: _.cloneDeep(state.childMap),
    handlerMap: _.cloneDeep(state.handlerMap),
    nextSiblingMap: _.cloneDeep(state.nextSiblingMap),
    nodeMap: _.cloneDeep(state.nodeMap),
    responseMap: _.cloneDeep(state.responseMap),
    slotMap: _.cloneDeep(state.slotMap)
  };
  slot.loading = true;
  slot.adding = true;
  addNodeHelper(newState, slot);
  return newState;
};

const addSlotError = (state, slot) => {
  const newState = {
    ...state,
    childMap: _.cloneDeep(state.childMap),
    handlerMap: _.cloneDeep(state.handlerMap),
    nodeMap: _.cloneDeep(state.nodeMap),
    responseMap: _.cloneDeep(state.responseMap),
    slotMap: _.cloneDeep(state.slotMap)
  };

  slot.adding = false;
  deleteNodeHelper(newState, slot);
  return newState;
};

const addSlotSuccess = (state, slot, inputHandler, focusHandler) => {
  let newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap)
  };
  slot.loading = false;
  slot.adding = false;
  newState = updateNodeHelper(newState, slot.dialog_node, slot);
  return addNodes(newState, [inputHandler, focusHandler]);
};

/**
 * This function checks if the provided message response is a single text item. If the only item in the message
 * response is a text response with an empty text value, then it will be removed.
 *
 * @param {object} messageResponse The message response object.
 */
function removeSingleEmptyTextResponse(messageResponse) {
  if (messageResponse.output.generic?.length !== 1) {
    return;
  }

  const item = messageResponse.output.generic[0];

  if (
    item.response_type === RESPONSE_TYPES.text &&
    item.values.length === 1 &&
    item.values[0].text === ''
  ) {
    messageResponse.output.generic[0].values = [];
  }
}

// UPDATE SLOT
const updateSlotStart = (state, slot, inputHandler, focusHandler) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap),
    pendingUpdatesMap: _.cloneDeep(state.pendingUpdatesMap)
  };
  const existingSlot = newState.nodeMap[slot.dialog_node];

  removeSingleEmptyTextResponse(focusHandler);

  turnOnLoadingHelper(newState, existingSlot.parent);
  turnOnLoadingHelper(newState, existingSlot.dialog_node);
  turnOnLoadingHelper(newState, inputHandler.dialog_node);
  turnOnLoadingHelper(newState, focusHandler.dialog_node);

  updateNodeHelper(newState, existingSlot.dialog_node, slot);
  updateNodeHelper(newState, inputHandler.dialog_node, inputHandler);
  updateNodeHelper(newState, focusHandler.dialog_node, focusHandler);

  selectNodeHelper(newState, existingSlot.dialog_node, false, false);
  selectNodeHelper(newState, existingSlot.parent, true, true);

  return newState;
};

const updateSlotSuccess = (state, slot, inputHandler, focusHandler) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap),
    pendingUpdatesMap: _.cloneDeep(state.pendingUpdatesMap)
  };
  const existingSlot = newState.nodeMap[slot.dialog_node];

  turnOffLoadingHelper(newState, existingSlot.parent);
  turnOffLoadingHelper(newState, existingSlot.dialog_node);
  turnOffLoadingHelper(newState, inputHandler.dialog_node);
  turnOffLoadingHelper(newState, focusHandler.dialog_node);

  return newState;
};

const updateSlotAndHandlersSuccess = (state, slotAndHandlers) => {
  const newState = {
    ...state,
    childMap: _.cloneDeep(state.childMap),
    handlerMap: _.cloneDeep(state.handlerMap),
    nextSiblingMap: _.cloneDeep(state.nextSiblingMap),
    nodeMap: _.cloneDeep(state.nodeMap),
    pendingUpdatesMap: _.cloneDeep(state.pendingUpdatesMap),
    responseMap: _.cloneDeep(state.responseMap),
    slotMap: _.cloneDeep(state.slotMap)
  };

  deleteNodeHelper(newState, slotAndHandlers[0], true, true);
  addNodesHelper(newState, slotAndHandlers);
  selectNodeHelper(newState, slotAndHandlers[0].parent, true, true);

  slotAndHandlers.forEach(node => {
    turnOffLoadingHelper(newState, node.dialog_node);
  });
  turnOffLoadingHelper(newState, slotAndHandlers[0].parent);

  return newState;
};

const updateSlotAndHandlersError = (state, slotAndHandlers) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap),
    pendingUpdatesMap: _.cloneDeep(state.pendingUpdatesMap)
  };

  slotAndHandlers.forEach(node => {
    turnOffLoadingHelper(newState, node.dialog_node);
  });
  turnOffLoadingHelper(newState, slotAndHandlers[0].parent);

  return newState;
};

// ENABLE MCR
const enableMcrStart = (state, node) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap)
  };

  newState.nodeMap[node.dialog_node].loading = true;
  return newState;
};

const enableMcrSuccess = (state, node, responses) => {
  let newState = {
    ...state,
    nextSiblingMap: _.cloneDeep(state.nextSiblingMap),
    nodeMap: _.cloneDeep(state.nodeMap)
  };

  newState.nodeMap[node.dialog_node].loading = false;
  newState = updateNodeHelper(newState, node.dialog_node, node);
  const newResponses = !state.responseMap[node.dialog_node];
  for (let i = 0; i < responses?.length; i++) {
    newState = updateNodeHelper(
      newState,
      responses[i].dialog_node,
      responses[i]
    );
    if (newResponses) {
      newState = addResponseStart(newState, responses[i]);
      newState = addResponseSuccess(newState, responses[i]);
    }
  }
  return newState;
};

// ENABLE SLOTS
const enableSlotsStart = (state, node) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap)
  };

  newState.nodeMap[node.dialog_node].loading = true;
  return newState;
};

const enableSlotsSuccess = (state, nodeId, node) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap)
  };
  const { type, digress_out_slots } = node;
  updateNodeHelper(newState, nodeId, {
    type,
    digress_out_slots,
    loading: false
  });

  return newState;
};

// ENABLE CALLOUT
const defaultWebhookCallout = {
  type: 'webhook',
  name: 'main_webhook',
  parameters: {},
  result_variable: 'webhook_result_'
};

const defaultActionCallout = {
  type: 'assistant_action',
  name: 'Untitled',
  parameters: {},
  result_variable: 'action_result_',
  assistant_action_settings: {
    use_global_variables: true,
    lookup_action_by_title: true
  }
};

const enableCalloutStart = (state, node, updatedNode, calloutInfo, action) => {
  const newState = {
    ...state,
    callouts: _.cloneDeep(state.callouts),
    nodeMap: _.cloneDeep(state.nodeMap)
  };

  newState.nodeMap[node.dialog_node].loading = true;
  setResponseNodesAttribute(newState, node.dialog_node, 'loading', true);

  updatedNode.metadata = node.metadata; // note: metadata may contain mcr customization info
  if (calloutInfo && calloutInfo.enableCallout) {
    const calloutType = calloutInfo.type;

    const callout =
      calloutType === 'webhook'
        ? _.cloneDeep(defaultWebhookCallout)
        : _.cloneDeep(defaultActionCallout);

    callout.result_variable = callout.result_variable + getNextCalloutId(state);
    updatedNode.actions = [callout];
  } else {
    if (updatedNode && updatedNode.metadata && updatedNode.metadata.callout) {
      // note: this won't be executed for a newly created dialog skill
      delete updatedNode.metadata.callout;
    }
    updatedNode.actions = null;
  }

  const responseIds = state.responseMap[node.dialog_node];
  if (responseIds) {
    action.responses = Array.from(responseIds, nodeId => state.nodeMap[nodeId]);
  }

  return newState;
};

const enableCalloutSuccess = (state, node, responses, newResponses, action) => {
  let newState = {
    ...state,
    childMap: _.cloneDeep(state.childMap),
    handlerMap: _.cloneDeep(state.handlerMap),
    nextSiblingMap: _.cloneDeep(state.nextSiblingMap),
    nodeMap: _.cloneDeep(state.nodeMap),
    responseMap: _.cloneDeep(state.responseMap),
    slotMap: _.cloneDeep(state.slotMap)
  };

  newState = updateNodeHelper(newState, node.dialog_node, node);

  for (let i = 0; i < responses?.length; i++) {
    newState = updateNodeHelper(
      newState,
      responses[i].dialog_node,
      responses[i]
    );
  }

  if (newResponses) {
    for (let i = 0; i < newResponses.length; i++) {
      addNodeHelper(newState, newResponses[i]);
    }
  }

  const responseIds = newState.responseMap[node.dialog_node];
  if (responseIds) {
    action.responses = Array.from(
      responseIds,
      nodeId => newState.nodeMap[nodeId]
    );
  }

  newState.nodeMap[node.dialog_node].loading = false;
  setResponseNodesAttribute(newState, node.dialog_node, 'loading', false);

  return newState;
};

// ADD RESPONSE
const addResponseStart = (state, newResponse) => {
  const newState = {
    ...state,
    childMap: _.cloneDeep(state.childMap),
    handlerMap: _.cloneDeep(state.handlerMap),
    nextSiblingMap: _.cloneDeep(state.nextSiblingMap),
    nodeMap: _.cloneDeep(state.nodeMap),
    responseMap: _.cloneDeep(state.responseMap),
    slotMap: _.cloneDeep(state.slotMap)
  };
  newResponse.loading = true;
  addNodeHelper(newState, newResponse);
  return newState;
};

const addResponseSuccess = (state, newResponse) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap)
  };

  newResponse.loading = false;
  newState.nodeMap[newResponse.dialog_node] = newResponse;
  return newState;
};

// MOVE SUBNODE
const moveSubnodeStart = (state, subnode) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap)
  };
  newState.nodeMap[subnode.dialog_node].loading = true;
  return newState;
};

const moveSubnodeSuccess = (state, subnode, direction) => {
  const newState = {
    ...state,
    nextSiblingMap: _.cloneDeep(state.nextSiblingMap),
    nodeMap: _.cloneDeep(state.nodeMap)
  };
  // We need to get the right subnodes depending on the type.
  // Note that the subnodes are referred to as "responses" in the rest
  // of this code to avoid even more confusing overly generic naming
  const response = newState.nodeMap[subnode.dialog_node];
  let responses;
  if (subnode.type === 'response_condition') {
    responses = newState.responseMap[response.parent];
  } else if (subnode.type === 'slot') {
    responses = newState.slotMap[response.parent];
  } else if (subnode.type === 'event_handler') {
    responses = newState.handlerMap[response.parent];
  }
  const responseIndex = responses.indexOf(subnode.dialog_node);
  if (direction === 'up' && responseIndex > 0) {
    // Find previous RESPONSE (may not be the direct previous_sibling)
    let previousResponse = newState.nodeMap[response.previous_sibling];
    while (previousResponse && previousResponse.type !== subnode.type) {
      previousResponse = newState.nodeMap[previousResponse.previous_sibling];
    }
    // Fix up previous_sibling reference of next sibling
    const nextSiblingId =
      newState.nextSiblingMap[
        getNextSiblingMapKey(response.parent, response.dialog_node)
      ];
    const nextSibling = newState.nodeMap[nextSiblingId];
    if (nextSibling) {
      nextSibling.previous_sibling = response.previous_sibling;
      newState.nextSiblingMap[
        getNextSiblingMapKey(response.parent, response.previous_sibling)
      ] = nextSibling.dialog_node;
    } else {
      newState.nextSiblingMap[
        getNextSiblingMapKey(response.parent, response.previous_sibling)
      ] = null;
    }
    // Insert response before previous RESPONSE
    response.previous_sibling = previousResponse.previous_sibling;
    newState.nextSiblingMap[
      getNextSiblingMapKey(response.parent, previousResponse.previous_sibling)
    ] = response.dialog_node;
    previousResponse.previous_sibling = response.dialog_node;
    newState.nextSiblingMap[
      getNextSiblingMapKey(response.parent, response.dialog_node)
    ] = previousResponse.dialog_node;
    // Reorder the response array
    responses.splice(responseIndex, 1);
    responses.splice(responseIndex - 1, 0, response.dialog_node);
  } else if (direction === 'down' && responseIndex < responses.length - 1) {
    // Find next RESPONSE
    let nextResponseId =
      newState.nextSiblingMap[
        getNextSiblingMapKey(response.parent, response.dialog_node)
      ];
    let nextResponse = newState.nodeMap[nextResponseId];
    while (nextResponse && nextResponse.type !== subnode.type) {
      nextResponseId =
        newState.nextSiblingMap[
          getNextSiblingMapKey(nextResponse.parent, nextResponse.dialog_node)
        ];
      nextResponse = newState.nodeMap[nextResponseId];
    }
    // Fix up previous_sibling reference of next sibling
    const nextSiblingId2 =
      newState.nextSiblingMap[
        getNextSiblingMapKey(response.parent, response.dialog_node)
      ];
    const nextSibling2 = newState.nodeMap[nextSiblingId2];
    if (nextSibling2) {
      nextSibling2.previous_sibling = response.previous_sibling;
      newState.nextSiblingMap[
        getNextSiblingMapKey(response.parent, response.previous_sibling)
      ] = nextSibling2.dialog_node;
    }
    // Fix up previous_sibling reference of next sibling of next response
    const nextResponseNextSiblingId =
      newState.nextSiblingMap[
        getNextSiblingMapKey(nextResponse.parent, nextResponse.dialog_node)
      ];
    const nextResponseNextSibling = newState.nodeMap[nextResponseNextSiblingId];
    if (nextResponseNextSibling) {
      nextResponseNextSibling.previous_sibling = response.dialog_node;
      newState.nextSiblingMap[
        getNextSiblingMapKey(response.parent, response.dialog_node)
      ] = nextResponseNextSibling.dialog_node;
    } else {
      newState.nextSiblingMap[
        getNextSiblingMapKey(response.parent, response.dialog_node)
      ] = null;
    }
    // Insert response before previous RESPONSE
    response.previous_sibling = nextResponse.dialog_node;
    newState.nextSiblingMap[
      getNextSiblingMapKey(response.parent, nextResponse.dialog_node)
    ] = response.dialog_node;
    // Reorder the response array
    responses.splice(responseIndex, 1);
    responses.splice(responseIndex + 1, 0, response.dialog_node);
  }
  newState.nodeMap[subnode.dialog_node].loading = false;
  return newState;
};

// ADD HANDLERS (batch update)
const updateHandlersStart = (
  state,
  nodeId,
  handlerType,
  existingHandlers,
  handlers
) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap)
  };
  for (let i = 0; i < existingHandlers.length; i++) {
    const handler = newState.nodeMap[existingHandlers[i].dialog_node];
    if (handler) {
      handler.loading = true;
    }
  }
  if (handlers) {
    for (let i = 0; i < handlers.length; i++) {
      handlers[i].loading = true;
      newState.nodeMap[handlers[i].dialog_node] = handlers[i];
    }
  }
  return newState;
};

const updateHandlersSuccess = (
  state,
  nodeId,
  handlerType,
  existingHandlers,
  handlers
) => {
  const newState = {
    ...state,
    childMap: _.cloneDeep(state.childMap),
    handlerMap: _.cloneDeep(state.handlerMap),
    nextSiblingMap: _.cloneDeep(state.nextSiblingMap),
    nodeMap: _.cloneDeep(state.nodeMap),
    responseMap: _.cloneDeep(state.responseMap),
    slotMap: _.cloneDeep(state.slotMap)
  };

  // Remove existing handlers of this type from the parent node
  for (let i = 0; i < existingHandlers.length; i++) {
    deleteNodeHelper(
      newState,
      newState.nodeMap[existingHandlers[i].dialog_node]
    );
  }

  // Find last child of the node
  let lastSiblingId;
  while (newState.nextSiblingMap[getNextSiblingMapKey(nodeId, lastSiblingId)]) {
    lastSiblingId =
      newState.nextSiblingMap[getNextSiblingMapKey(nodeId, lastSiblingId)];
  }

  // Add the new handlers at the end of ALL child nodes/subnodes
  for (let i = 0; i < handlers.length; i++) {
    handlers[i].loading = false;
    if (i === 0) {
      handlers[i].previous_sibling = lastSiblingId;
    } else {
      handlers[i].previous_sibling = handlers[i - 1].dialog_node;
    }
    addNodeHelper(newState, handlers[i]);
  }
  return newState;
};

const updateJumpToStart = (state, updatedJumpTo, noSave) => {
  const newState = {
    ...state,
    nodeMap: _.cloneDeep(state.nodeMap),
    pendingUpdatesMap: _.cloneDeep(state.pendingUpdatesMap)
  };

  const newJumpToMode = {
    ...state.jumpToMode
  };

  if (noSave === true) {
    newJumpToMode.noSave = true;
  }

  const { sourceNode, targetNode, selector } = updatedJumpTo;

  if (Object.keys(updatedJumpTo).length === 0 || updatedJumpTo.sourceNode) {
    newJumpToMode.isActive = false;
    newJumpToMode.sourceNode = null;
    newJumpToMode.targetNode = null;
    newJumpToMode.selector = null;
    newJumpToMode.nextStep = null;
  }

  if (sourceNode) {
    newJumpToMode.isActive = true;
    newJumpToMode.sourceNode = sourceNode;
  }

  if (targetNode) {
    newJumpToMode.targetNode = targetNode;
  }

  if (selector) {
    turnOnLoadingHelper(newState, newJumpToMode.sourceNode.dialog_node);
    newJumpToMode.selector = selector;
  }

  newState.jumpToMode = newJumpToMode;

  return newState;
};

const updateJumpToSuccess = (state, node) => {
  const newState = {
    ...state,
    jumpToMode: _.cloneDeep(state.jumpToMode),
    nodeMap: _.cloneDeep(state.nodeMap),
    pendingUpdatesMap: _.cloneDeep(state.pendingUpdatesMap)
  };

  if (state.jumpToMode.selector) {
    turnOffLoadingHelper(newState, state.jumpToMode.sourceNode.dialog_node);

    const newJumpToMode = {
      ...state.jumpToMode,
      noSave: false,
      isActive: false,
      sourceNode: null,
      targetNode: null,
      selector: null
    };

    if (node && state.jumpToMode.noSave !== true) {
      const { next_step } = node;
      updateNodeHelper(newState, node.dialog_node, { next_step });
    } else if (node && state.jumpToMode.noSave === true) {
      const { next_step } = node;
      newJumpToMode.nextStep = {
        source_node: state.jumpToMode.sourceNode,
        target_node: state.jumpToMode.targetNode,
        selector: next_step.selector,
        behavior: next_step.behavior
      };
    }

    newState.jumpToMode = newJumpToMode;
  } else {
    newState.jumpToMode = { ...state.jumpToMode };
  }

  return newState;
};

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionType.CLEAR_NODES:
      return clearNodes(state, action.preserveSelectedNode);
    case actionType.ADD_NODES:
      return addNodes(state, action.newNodes);
    case actionType.ADD_NODES_CACHE:
      return addNodesCache(state);
    case actionType.ADD_NODE_START:
      return addNodeStart(state, action.newNode, action.select);
    case actionType.ADD_NODE_ERROR:
      return addNodeError(state, action.newNode, action.select);
    case actionType.ADD_NODE_SUCCESS:
      return addNodeSuccess(state, action.newNode, action.select);
    case actionType.ADD_RESPONSE_START:
      return addResponseStart(state, action.newResponse);
    case actionType.ADD_RESPONSE_SUCCESS:
      return addResponseSuccess(state, action.newResponse);
    case actionType.ADD_SLOT_START:
      return addSlotStart(state, action.newSlot);
    case actionType.ADD_SLOT_ERROR:
      return addSlotError(state, action.newSlot);
    case actionType.ADD_SLOT_SUCCESS:
      return addSlotSuccess(
        state,
        action.newSlot,
        action.inputHandler,
        action.focusHandler
      );
    case actionType.DELETE_NODE_START:
      return deleteNodeStart(state, action.node);
    case actionType.DELETE_NODE_SUCCESS:
      return deleteNodeSuccess(state, action.node, action.childNodesToPreserve);
    case actionType.EXPAND_NODE:
      return expandNode(state, action.nodeId, action.expanded);
    case actionType.EXPAND_PATH:
      return expandPath(state, action.path, action.expanded);
    case actionType.SELECT_NODE:
      return selectNode(
        state,
        action.nodeId,
        action.selected,
        action.nodeIdsToHighlight
      );
    case actionType.SELECT_NODES:
      return selectNodes(
        state,
        action.nodesWithAncestors,
        action.nodesSelected
      );
    case actionType.SELECT_NODES_WITH_ERRORS:
      return selectNodeWithErrors(state, action.nodeId);
    case actionType.UPDATE_NODE_START:
      return updateNodeStart(
        state,
        action.nodeId,
        action.useNewIAUpdatedAccess,
        action.assistantId,
        action.node
      );
    case actionType.UPDATE_NODE_SUCCESS:
      return updateNodeSuccess(state, action.nodeId, action.node);
    case actionType.UPDATE_NODE_ERROR:
      return updateNodeError(state, action.nodeId, action.node);
    case actionType.MOVE_NODE_START:
      return moveNodeStart(
        state,
        action.sourceNode,
        action.targetNode,
        action.where
      );
    case actionType.MOVE_NODE_SUCCESS:
      return moveNodeSuccess(state, action.nodeId);
    case actionType.MOVE_NODE_ERROR:
      return moveNodeError(state, action.sourceNode, action.undoNode);
    case actionType.DUPLICATE_NODE_START:
      return duplicateNodeStart(
        state,
        action.sourceNode,
        action.newNode,
        action.allDuplicates
      );
    case actionType.DUPLICATE_NODE_SUCCESS:
      return duplicateNodeSuccess(state, action.nodeId);
    case actionType.DUPLICATE_NODE_ERROR:
      return duplicateNodeError(state, action.newNode);
    case actionType.UPDATE_JUMP_TO_START:
      return updateJumpToStart(state, action.updatedJumpTo, action.noSave);
    case actionType.UPDATE_JUMP_TO_SUCCESS:
      return updateJumpToSuccess(state, action.node);
    case actionType.UPDATE_NODES:
      return updateNodes(state, action.updatedNodes);
    case actionType.UPDATE_SLOT_START:
      return updateSlotStart(
        state,
        action.slot,
        action.inputHandler,
        action.focusHandler
      );
    case actionType.UPDATE_SLOT_SUCCESS:
      return updateSlotSuccess(
        state,
        action.slot,
        action.inputHandler,
        action.focusHandler
      );
    case actionType.UPDATE_HANDLERS_START:
      return updateHandlersStart(
        state,
        action.nodeId,
        action.handlerType,
        action.existingHandlers,
        action.handlers
      );
    case actionType.UPDATE_HANDLERS_SUCCESS:
      return updateHandlersSuccess(
        state,
        action.nodeId,
        action.handlerType,
        action.existingHandlers,
        action.handlers
      );
    case actionType.MOVE_SUBNODE_START:
      return moveSubnodeStart(state, action.subnode, action.direction);
    case actionType.MOVE_SUBNODE_SUCCESS:
      return moveSubnodeSuccess(state, action.subnode, action.direction);
    case actionType.ENABLE_MCR_START:
      return enableMcrStart(state, action.node, action.enable);
    case actionType.ENABLE_MCR_SUCCESS:
      return enableMcrSuccess(
        state,
        action.node,
        action.responses,
        action.enable
      );
    case actionType.ENABLE_SLOTS_START:
      return enableSlotsStart(state, action.node);
    case actionType.ENABLE_SLOTS_SUCCESS:
      return enableSlotsSuccess(state, action.nodeId, action.node);
    case actionType.UPDATE_SLOT_AND_HANDLERS_SUCCESS:
      return updateSlotAndHandlersSuccess(state, action.slotAndHandlers);
    case actionType.UPDATE_SLOT_AND_HANDLERS_ERROR:
      return updateSlotAndHandlersError(state, action.slotAndHandlers);
    case actionType.ENABLE_CALLOUT_START:
      return enableCalloutStart(
        state,
        action.node,
        action.updatedNode,
        action.calloutInfo,
        action.action
      );
    case actionType.ENABLE_CALLOUT_SUCCESS:
      return enableCalloutSuccess(
        state,
        action.node,
        action.responses,
        action.newResponses,
        action.action
      );
    default:
      return state;
  }
};

export default reducer;
